import React, { useState, useEffect } from "react";
import { PowerSkillPdfStatusResponse } from "../../typings/powerskill";
import { powerSkillService } from "../../services/powerskill.service";
import Button from "../UI/Button";
import { FileDown, Loader2, AlertCircle } from "lucide-react";

interface PowerSkillPdfDownloadProps {
  testId: number;
  studentName?: string;
}

const PowerSkillPdfDownload: React.FC<PowerSkillPdfDownloadProps> = ({
  testId,
  studentName = "Student",
}) => {
  const [status, setStatus] = useState<PowerSkillPdfStatusResponse["status"]>("not_queued");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(() => {
      if (status === "pending" || status === "processing") {
        checkStatus();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [status, testId]);

  const checkStatus = async () => {
    try {
      const response = await powerSkillService.getPdfStatus(testId);
      setStatus(response.status);
    } catch (err) {
      console.error("Error checking PDF status:", err);
    }
  };

  const handleDownload = async () => {
    if (status !== "completed") return;
    setLoading(true);
    setError(null);
    try {
      const blob = await powerSkillService.downloadPdf(testId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `PowerSkill-Result-${studentName}-${testId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError("Gagal mengunduh PDF");
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return "Mengunduh...";
    switch (status) {
      case "pending":
      case "processing":
        return "Menyiapkan PDF...";
      case "completed":
        return "Unduh Laporan PDF";
      case "failed":
        return "Gagal Menyiapkan PDF";
      default:
        return "Unduh Laporan PDF";
    }
  };

  const isDisabled = loading || status === "pending" || status === "processing" || status === "failed" || status === "not_queued";

  return (
    <div className="w-full">
      <Button
        outline
        className={`w-full py-4 font-bold flex items-center justify-center gap-2 border-2 gradient-accent text-white`}
        onClick={handleDownload}
        disabled={isDisabled}
      >
        {loading || status === "pending" || status === "processing" ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <FileDown className="w-5 h-5" />
        )}
        {getButtonText()}
      </Button>
      {error && (
        <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
};

export default PowerSkillPdfDownload;
